import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import toast from "react-hot-toast";
import { useMyListings } from "../../hooks/last-minute/useMyListings";
import ListingComposer from "../../components/last-minute/ListingComposer";
import ScoutInfoDot from "../../components/scout-request/ScoutInfoDot";
import AnonSilhouetteAvatar from "../../components/last-minute/AnonSilhouetteAvatar";
import ImageOrInitials from "../../components/agency/ImageOrInitials";
import { UrgencyPill } from "../../components/last-minute/LmPills";
import OwnerStatsStrip from "../../components/last-minute/OwnerStatsStrip";
import { PlusMarkGlyph } from "../../components/last-minute/lmGlyphs";
import { ROLES } from "../../config/roles";
import { listingCountdown, urgencyMeta } from "../../utils/lastMinute";

// ── My Listings (LM design 5a–5c + Mobile 8h), presentation rebuilt 1:1 from the
// handoff. Data + lifecycle actions (extend/renew/unpublish, AgencyAdmin scope)
// are untouched. Desktop = one management row per listing; <sm = the 8h stacked
// card. The Active tab groups any expired listings under an "EXPIRED" divider
// exactly as board 5a draws it; the Expired tab shows them alone.

const shortDate = (raw) => {
  if (!raw) return "";
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? "" : format(d, "MMM d");
};

const publishedToday = (raw) => {
  if (!raw) return false;
  const d = new Date(raw);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate()
  );
};

// Action-button palette (5a): Extend = blue-outline (amber row → solid blue),
// Edit = neutral outline, Unpublish/Remove = muted outline, Renew = solid blue.
const btn = {
  extend: "rounded-[10px] border border-[#BDD9FF] bg-white px-3.5 py-2 text-[12.5px] font-semibold text-[#1466D8] hover:bg-[#F0F7FF]",
  extendUrgent: "rounded-[10px] bg-[#2283FF] px-3.5 py-2 text-[12.5px] font-semibold text-white hover:bg-[#1B6FE0]",
  edit: "rounded-[10px] border border-[#D8E0EA] bg-white px-3.5 py-2 text-[12.5px] font-semibold text-[#33475E] hover:bg-gray-50",
  unpublish: "rounded-[10px] border border-[#D8E0EA] bg-white px-3.5 py-2 text-[12.5px] font-semibold text-[#8A98AC] hover:bg-gray-50",
  renew: "rounded-[10px] bg-[#2283FF] px-4 py-2 text-[12.5px] font-semibold text-white hover:bg-[#1B6FE0]",
};

// One management row (active or expired) — desktop 5a row / mobile 8h card.
const ListingRow = ({ listing, expired, onExtend, onRenew, onUnpublish, onEdit }) => {
  const countdown = listingCountdown(listing);
  const amber = !expired && countdown.urgent;
  const isNew = !expired && publishedToday(listing.created_at);
  const anon = !!listing.anonymized;
  const subject = anon
    ? [listing.position, listing.age].filter(Boolean).join(", ")
    : listing.player_name || "Player";
  const revealed = listing.stats?.reveals ?? 0;
  // Meta line (5a): named → "ST · 27 · Named listing [· Not ready for CH, RU]";
  // anonymized → "Elias Brandt · Anonymized · revealed to N clubs".
  const notReady = (listing.not_ready_for || [])
    .map((c) => c.code || c.name)
    .filter(Boolean)
    .map((s) => String(s).toUpperCase());
  const metaBits = anon
    ? [
        listing.player_name,
        expired
          ? `Anonymized · was revealed to ${revealed} club${revealed === 1 ? "" : "s"}`
          : `Anonymized · revealed to ${revealed} club${revealed === 1 ? "" : "s"}`,
      ]
    : [
        listing.position,
        listing.age != null ? `${listing.age}` : null,
        "Named listing",
        notReady.length ? `Not ready for ${notReady.join(", ")}` : null,
      ];
  const meta = metaBits.filter(Boolean).join(" · ");

  const avatar = anon ? (
    <span className={expired ? "opacity-80 grayscale-[40%] inline-flex" : "inline-flex"}>
      <AnonSilhouetteAvatar size={44} />
    </span>
  ) : (
    <span className={`inline-flex flex-none [&>*]:!w-11 [&>*]:!h-11 ${expired ? "opacity-85 grayscale-[60%]" : ""}`}>
      <ImageOrInitials src={listing.player_photo || null} alt={subject} size="large" />
    </span>
  );

  // Expiry descriptor (5a right column).
  const expiry = expired ? (
    <span className="text-[12.5px] font-semibold text-[#98A5B8] whitespace-nowrap">
      Expired{listing.expires_at ? ` on ${shortDate(listing.expires_at)}` : ""}
    </span>
  ) : amber ? (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FBF1DB] px-3 py-1 text-[12.5px] font-bold text-[#92650A] whitespace-nowrap">
      <span className="w-[5px] h-[5px] rounded-full bg-[#E3A11C]" />
      {countdown.label}
    </span>
  ) : (
    <span className="text-[12.5px] font-semibold text-[#5A6B82] whitespace-nowrap">
      {isNew ? "Published today · 14 days" : countdown.label}
    </span>
  );

  const actions = expired ? (
    <>
      <button onClick={() => onRenew(listing)} className={btn.renew}>
        Renew for 14 days
      </button>
      <button onClick={() => onUnpublish(listing)} className={btn.unpublish}>
        Remove
      </button>
    </>
  ) : (
    <>
      <button onClick={() => onExtend(listing)} className={amber ? btn.extendUrgent : btn.extend}>
        Extend 14 days
      </button>
      <button onClick={() => onEdit(listing)} className={btn.edit}>
        Edit
      </button>
      <button onClick={() => onUnpublish(listing)} className={btn.unpublish}>
        Unpublish
      </button>
    </>
  );

  return (
    <div
      className={`rounded-2xl border shadow-[0_1px_2px_rgba(16,24,40,.05)] px-4 py-4 sm:px-5 ${
        expired ? "border-[#E4E9F1] bg-[#F8FAFC] shadow-none" : amber ? "border-[#F0D9A8] bg-white" : "border-[#E4E9F1] bg-white"
      }`}
    >
      {/* ≥sm: one 5a row */}
      <div className="hidden sm:flex items-center gap-4 flex-wrap xl:flex-nowrap">
        {avatar}
        <div className="w-[220px] lg:w-[300px] min-w-0 flex-none">
          <div className="flex items-center gap-1.5 min-w-0">
            <p className={`text-[14.5px] font-bold truncate ${expired ? "text-[#33475E]" : "text-[#14263D]"}`}>{subject}</p>
            {isNew && anon && (
              <span className="rounded-[5px] bg-[#E9F2FF] px-1.5 py-0.5 text-[10px] font-bold tracking-[0.05em] text-[#1466D8] flex-none">
                NEW
              </span>
            )}
          </div>
          <p className={`text-[12px] truncate mt-0.5 ${expired ? "text-[#98A5B8]" : "text-[#8A98AC]"}`}>{meta}</p>
        </div>
        <UrgencyPill urgency={listing.urgency_class} expired={expired} />
        <OwnerStatsStrip stats={listing.stats} expired={expired} />
        <div className="ml-auto flex items-center gap-4">
          {expiry}
          <div className="flex items-center gap-2">{actions}</div>
        </div>
      </div>

      {/* <sm: the 8h card */}
      <div className="sm:hidden flex flex-col gap-3">
        <div className="flex items-center gap-3">
          {avatar}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              <p className={`text-[14.5px] font-bold truncate ${expired ? "text-[#33475E]" : "text-[#14263D]"}`}>{subject}</p>
              {isNew && anon && (
                <span className="rounded-[5px] bg-[#E9F2FF] px-1.5 py-0.5 text-[10px] font-bold tracking-[0.05em] text-[#1466D8] flex-none">
                  NEW
                </span>
              )}
            </div>
            <p className={`text-[11.5px] truncate mt-0.5 ${expired ? "text-[#98A5B8]" : "text-[#8A98AC]"}`}>
              {expired ? [listing.position, listing.age, `Expired on ${shortDate(listing.expires_at)}`].filter(Boolean).join(" · ") : meta}
            </p>
          </div>
          {expired ? (
            <button onClick={() => onRenew(listing)} className="rounded-[10px] bg-[#2283FF] px-3.5 py-2 text-[12px] font-semibold text-white flex-none">
              Renew
            </button>
          ) : amber ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FBF1DB] px-2.5 py-1 text-[11px] font-bold text-[#92650A] flex-none whitespace-nowrap">
              <span className="w-1 h-1 rounded-full bg-[#E3A11C]" />
              {countdown.days} day{countdown.days === 1 ? "" : "s"} left
            </span>
          ) : (
            <UrgencyPill urgency={listing.urgency_class} />
          )}
        </div>
        {!expired && (
          <>
            <OwnerStatsStrip stats={listing.stats} className="w-full justify-between" />
            {amber ? (
              <div className="flex gap-2">
                <button onClick={() => onExtend(listing)} className="flex-1 rounded-[10px] bg-[#2283FF] py-2.5 text-center text-[12.5px] font-semibold text-white">
                  Extend 14 days
                </button>
                <button onClick={() => onUnpublish(listing)} className="rounded-[10px] border border-[#D8E0EA] bg-white px-3.5 py-2.5 text-[12.5px] font-semibold text-[#8A98AC]">
                  Unpublish
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-[12px] font-semibold text-[#5A6B82] mr-auto">
                  {isNew ? "Published today · 14 days" : countdown.label}
                </span>
                <button onClick={() => onExtend(listing)} className="rounded-[10px] border border-[#BDD9FF] bg-white px-3 py-2 text-[12px] font-semibold text-[#1466D8]">
                  Extend 14 days
                </button>
                <button onClick={() => onEdit(listing)} className="rounded-[10px] border border-[#D8E0EA] bg-white px-3 py-2 text-[12px] font-semibold text-[#33475E]">
                  Edit
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Unpublish confirm (design 5b) — keep-live duration wording, incl. the absolute
// "until" date the artboard draws in bold.
const UnpublishDialog = ({ listing, onConfirm, onCancel }) => {
  if (!listing) return null;
  const countdown = listingCountdown(listing);
  const days = Math.max(countdown.days ?? 0, 0);
  const until = shortDate(listing.expires_at);
  const subject = listing.anonymized
    ? [listing.position, listing.age].filter(Boolean).join(", ")
    : listing.player_name || "Player";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(16,26,40,0.44)] p-4">
      <div className="bg-white rounded-[18px] w-full max-w-[400px] p-6 shadow-[0_32px_64px_-16px_rgba(16,24,40,0.35)] flex flex-col gap-3">
        <h3 className="text-[16px] font-bold text-[#14263D]">Unpublish this listing?</h3>
        <div className="flex items-center gap-2 rounded-[10px] border border-[#E4E9F1] bg-[#F8FAFC] px-3 py-2">
          {listing.anonymized ? (
            <AnonSilhouetteAvatar size={24} />
          ) : (
            <ImageOrInitials src={listing.player_photo || null} alt={subject} size="small" />
          )}
          <span className="text-[12px] font-semibold text-[#33475E] truncate">{subject}</span>
          <span className="text-[11.5px] text-[#8A98AC] whitespace-nowrap">
            · {urgencyMeta(listing.urgency_class).label} · {days}d left
          </span>
        </div>
        <p className="text-[13px] text-[#5A6B82] leading-[1.55]">
          It disappears from Last Minute immediately. Clubs you already revealed the profile to keep their chat with
          you. If you keep it live, nothing changes: it simply runs out its remaining{" "}
          <b className="text-[#33475E]">
            {days} day{days === 1 ? "" : "s"}
            {until ? ` (until ${until})` : ""}
          </b>
          , extendable anytime.
        </p>
        <div className="mt-1.5 flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            className="rounded-[10px] border border-[#D8E0EA] bg-white px-4 py-2 text-[13px] font-semibold text-[#33475E] hover:bg-gray-50"
          >
            Keep it live · {days}d left
          </button>
          <button
            onClick={() => onConfirm(listing)}
            className="rounded-[10px] bg-[#C4402F] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[#a83527]"
          >
            Unpublish
          </button>
        </div>
      </div>
    </div>
  );
};

const MyListingsPage = () => {
  const currentRole = useSelector((state) => state.auth.currentRole);
  const isAgencyAdmin = currentRole === ROLES.AgencyAdmin;
  const [searchParams, setSearchParams] = useSearchParams();

  const [scope, setScope] = useState("mine");
  const [scopeOpen, setScopeOpen] = useState(false);
  const [tab, setTab] = useState("active");
  const { listings, loading, extend, renew, unpublish, refetch } = useMyListings(scope);

  const [confirmUnpublish, setConfirmUnpublish] = useState(null);
  const [editListing, setEditListing] = useState(null);

  const active = useMemo(() => listings.filter((l) => l.status === "active"), [listings]);
  const expired = useMemo(() => listings.filter((l) => l.status === "expired"), [listings]);

  const composeParam = searchParams.get("compose");
  const composerOpen = composeParam === "1" || !!editListing;
  const openComposer = () => setSearchParams({ compose: "1" });
  const closeComposer = () => {
    setEditListing(null);
    const next = new URLSearchParams(searchParams);
    next.delete("compose");
    setSearchParams(next);
  };

  const doExtend = async (l) => {
    const res = await extend(l.id);
    toast[res.success ? "success" : "error"](res.success ? "Extended 14 days" : res.error);
  };
  const doRenew = async (l) => {
    const res = await renew(l.id);
    toast[res.success ? "success" : "error"](res.success ? "Renewed for 14 days" : res.error);
  };
  const doUnpublish = async (l) => {
    setConfirmUnpublish(null);
    const res = await unpublish(l.id);
    if (res.success) {
      toast.success("Unpublished");
      refetch();
    } else {
      toast.error(res.error);
    }
  };

  const rowProps = { onExtend: doExtend, onRenew: doRenew, onUnpublish: setConfirmUnpublish, onEdit: setEditListing };

  return (
    // Platform-standard container (W1-04): no fixed max-width, page scales
    // to 2560+ via the progressive padding ladder.
    <div className="mx-auto px-4 md:px-6 lg:px-7 xl:px-8 min-[1920px]:px-10 min-[2560px]:px-12 py-6">
      {/* Header (5a): title + serif info dot + "+ List a player" */}
      <div className="flex items-start justify-between gap-3 mb-1">
        <h1 className="flex items-center gap-2.5 text-[22px] sm:text-[27px] font-bold tracking-[-0.01em] text-[#14263D]">
          My Listings
          <ScoutInfoDot
            id="last-minute-my-listings"
            variant="lm"
            title="My Listings"
            body="Your Last Minute listings. Views and contacts are visible only to you; nothing here ever appears on your public profile."
          />
        </h1>
        <button
          onClick={openComposer}
          className="rounded-[10px] bg-[#2283FF] px-3.5 sm:px-[18px] py-2 sm:py-2.5 text-[12px] sm:text-[13.5px] font-semibold text-white shadow-[0_2px_6px_rgba(34,131,255,.35)] hover:bg-[#1B6FE0] flex-shrink-0"
        >
          +&nbsp;&nbsp;List a player
        </button>
      </div>
      <p className="text-[13.5px] text-[#5A6B82] mb-5">Your Last Minute listings; stats are visible only to you.</p>

      {/* Scope dropdown (AgencyAdmin, drawn open-state 5a) + Active/Expired tabs */}
      <div className="flex items-start gap-4 mb-6">
        {isAgencyAdmin && (
          <div className="relative z-20">
            <button
              onClick={() => setScopeOpen((v) => !v)}
              className={`inline-flex items-center gap-1.5 rounded-[10px] bg-white px-3.5 py-2 text-[12.5px] font-semibold text-[#14263D] ${
                scopeOpen
                  ? "border-[1.5px] border-[#2283FF] shadow-[0_0_0_3px_rgba(34,131,255,.12)]"
                  : "border border-[#E4E9F1] hover:bg-gray-50"
              }`}
            >
              {scope === "agency" ? "All agency listings" : "My listings"}
              <span className="text-[9px] text-[#8A98AC]">{scopeOpen ? "▲" : "▼"}</span>
            </button>
            {scopeOpen && (
              <div className="absolute top-[42px] left-0 w-[250px] rounded-xl border border-[#E4E9F1] bg-white shadow-[0_16px_36px_-10px_rgba(16,24,40,.25)] overflow-hidden">
                {[
                  { key: "mine", label: "My listings" },
                  { key: "agency", label: "All agency listings", badge: "AGENCYADMIN" },
                ].map((o, i) => {
                  const on = scope === o.key;
                  return (
                    <button
                      key={o.key}
                      onClick={() => {
                        setScope(o.key);
                        setScopeOpen(false);
                      }}
                      className={`w-full flex items-center gap-2 px-3.5 py-[11px] text-[12.5px] text-left ${
                        i > 0 ? "border-t border-[#F1F4F8]" : ""
                      } ${on ? "bg-[#F0F7FF] font-semibold text-[#1466D8]" : "text-[#33475E] hover:bg-gray-50"}`}
                    >
                      {o.label}
                      {o.badge && (
                        <span className="rounded-[5px] bg-[#EDF1F6] px-1.5 py-0.5 text-[9.5px] font-bold tracking-[0.05em] text-[#71829A]">
                          {o.badge}
                        </span>
                      )}
                      {on && <span className="ml-auto">✓</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
        <div className="flex gap-[18px] pt-2">
          {[
            { key: "active", label: "Active", count: active.length },
            { key: "expired", label: "Expired", count: expired.length },
          ].map((t) => {
            const on = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`relative pb-2.5 text-[13.5px] whitespace-nowrap ${
                  on ? "font-semibold text-[#14263D]" : "font-medium text-[#5A6B82]"
                }`}
              >
                {t.label} <span className={on ? "text-[#8A98AC]" : "text-[#98A5B8]"}>{t.count}</span>
                {on && <span className="absolute left-0 right-0 bottom-0 h-[2.5px] rounded-[2px] bg-[#2283FF]" />}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[0, 1].map((i) => (
            <div key={i} className="h-24 rounded-2xl bg-[#F1F4F8] animate-pulse" />
          ))}
        </div>
      ) : listings.length === 0 ? (
        // Empty state (5c) → the composer
        <div className="rounded-2xl border border-[#E4E9F1] bg-white px-6 py-[52px] flex flex-col items-center gap-2.5 text-center">
          <div className="w-[52px] h-[52px] rounded-full bg-[#EEF3FA] flex items-center justify-center mb-1.5">
            <PlusMarkGlyph size={20} />
          </div>
          <p className="text-[16.5px] font-bold text-[#14263D]">Nothing listed right now</p>
          <p className="text-[13px] text-[#5A6B82] leading-[1.55] max-w-[380px]">
            When one of your players or staff is ready for a move, list them here; clubs across 20+ countries see it the
            moment you publish.
          </p>
          <button
            onClick={openComposer}
            className="mt-2.5 rounded-[10px] bg-[#2283FF] px-5 py-2.5 text-[13px] font-semibold text-white hover:bg-[#1B6FE0]"
          >
            List a player
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {tab === "active" ? (
            <>
              {active.map((l) => (
                <ListingRow key={l.id} listing={l} expired={false} {...rowProps} />
              ))}
              {/* 5a: expired listings sit below the actives under an EXPIRED divider */}
              {expired.length > 0 && (
                <>
                  <div className="mt-2.5 flex items-center gap-2.5">
                    <span className="text-[12px] font-bold uppercase tracking-[0.08em] text-[#8A98AC]">Expired</span>
                    <span className="flex-1 h-px bg-[#E0E6EF]" />
                  </div>
                  {expired.map((l) => (
                    <ListingRow key={l.id} listing={l} expired {...rowProps} />
                  ))}
                </>
              )}
              {active.length === 0 && expired.length > 0 && (
                <p className="text-[13px] text-[#8A98AC] order-first">No active listings right now.</p>
              )}
            </>
          ) : expired.length === 0 ? (
            <p className="text-[13px] text-[#8A98AC]">No expired listings.</p>
          ) : (
            expired.map((l) => <ListingRow key={l.id} listing={l} expired {...rowProps} />)
          )}
        </div>
      )}

      <UnpublishDialog listing={confirmUnpublish} onConfirm={doUnpublish} onCancel={() => setConfirmUnpublish(null)} />
      <ListingComposer
        open={composerOpen}
        onClose={closeComposer}
        editListing={editListing}
        onPublished={() => refetch()}
      />
    </div>
  );
};

export default MyListingsPage;
